import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import RuleBuilderForm from "./RuleBuilderForm";
import { fulfillmentRuleService } from "../../../utils/services/fulfillmentRuleService";
import { EMPTY_RULE } from "../constants";

export default function RuleBuilderDialog({
  open,
  onClose,
  editingRule,
  onSaved,
  warehouses,
  clients,
  isAdmin,
  clientId,
}) {
  const [rule, setRule] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingRule) {
      setRule({ ...EMPTY_RULE, ...editingRule });
    } else {
      setRule({ ...EMPTY_RULE, client: isAdmin ? "" : clientId });
    }
  }, [editingRule, open, clientId, isAdmin]);

  const handleSave = async () => {
    if (!rule.name.trim()) {
      toast.error("Rule name is required");
      return;
    }
    if (isAdmin && !rule.client) {
      toast.error("Please select a client");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...rule, client: isAdmin ? rule.client : clientId };
      if (rule.warehouse_strategy === "PRIORITY") {
        payload.warehouse_priority = rule.warehouse_priority.map((p, i) => ({
          warehouse: p.warehouse,
          order: i + 1,
        }));
      } else {
        payload.warehouse_priority = [];
      }
      if (editingRule?._id) {
        await fulfillmentRuleService.update(editingRule._id, payload);
        toast.success("Fulfillment rule updated");
      } else {
        await fulfillmentRuleService.create(payload);
        toast.success("Fulfillment rule created");
      }
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, background: "var(--bg-paper)" },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}
      >
        <Box>
          <Typography fontFamily="Poppins, sans-serif" fontWeight={700} fontSize={17} color="var(--text-primary)">
            {editingRule ? "Edit Fulfillment Rule" : "Create Fulfillment Rule"}
          </Typography>
          <Typography fontFamily="Poppins, sans-serif" fontSize={12} color="var(--text-secondary)">
            Define how orders should be allocated to warehouses
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, overflowY: "auto", maxHeight: "72vh" }}>
        <RuleBuilderForm
          rule={rule}
          setRule={setRule}
          warehouses={warehouses}
          clients={clients}
          isAdmin={isAdmin}
        />
      </DialogContent>
      <Divider />
      <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ fontFamily: "Poppins, sans-serif", textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
          sx={{ fontFamily: "Poppins, sans-serif", textTransform: "none", minWidth: 120 }}
        >
          {editingRule ? "Save Changes" : "Create Rule"}
        </Button>
      </Box>
    </Dialog>
  );
}
